import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Hook to manage modal state via URL query parameters.
 * This enables browser back/forward navigation for modals.
 *
 * @param modalName - The name of the modal (used as the query param value)
 * @returns Object with isOpen state and open/close functions
 *
 * @example
 * // In a component:
 * const { isOpen, open, close } = useModalQuery('add-material');
 *
 * // URL will be: /library?modal=add-material when open
 */
export function useModalQuery(modalName: string) {
  const [searchParams, setSearchParams] = useSearchParams();

  const isOpen = searchParams.get('modal') === modalName;

  const open = useCallback(() => {
    setSearchParams(
      (prev) => {
        prev.set('modal', modalName);
        return prev;
      },
      { replace: false }
    );
  }, [modalName, setSearchParams]);

  const close = useCallback(() => {
    setSearchParams(
      (prev) => {
        prev.delete('modal');
        return prev;
      },
      { replace: true }
    ); // Use replace to avoid adding history entry when closing
  }, [setSearchParams]);

  return { isOpen, open, close };
}

/**
 * Hook to manage panel state via URL query parameters.
 * Similar to useModalQuery but for sidebar panels that use a different param name.
 *
 * @param panelName - The name of the panel (e.g., 'favorites', 'inbox')
 * @returns Object with isOpen state and open/close/toggle functions
 *
 * @example
 * const { isOpen, open, close, toggle } = usePanelQuery('favorites');
 *
 * // URL will be: /study?favorites=open when open
 */
export function usePanelQuery(panelName: string) {
  const [searchParams, setSearchParams] = useSearchParams();

  const isOpen = searchParams.get(panelName) === 'open';

  const open = useCallback(() => {
    setSearchParams(
      (prev) => {
        prev.set(panelName, 'open');
        return prev;
      },
      { replace: false }
    );
  }, [panelName, setSearchParams]);

  const close = useCallback(() => {
    setSearchParams(
      (prev) => {
        prev.delete(panelName);
        return prev;
      },
      { replace: true }
    );
  }, [panelName, setSearchParams]);

  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, open, close]);

  return { isOpen, open, close, toggle };
}
